import { computed, effect, inject, InjectionToken } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {
  addEntities,
  addEntity,
  removeAllEntities,
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import {
  divIcon,
  GeoJSON,
  latLng,
  latLngBounds,
  LatLngExpression,
  LatLngTuple,
  Layer,
  LayerGroup,
  Marker,
  PM,
  Polygon,
  Polyline,
  Rectangle,
} from 'leaflet';
import { MapService } from './map';
import {
  type MarkupLastUsedStylesMap,
  MarkupElement,
  MarkupPointElement,
  MarkupTypes,
} from './markup.model';
import { MarkupService } from './markup.service';

export const MARKUP_STORE_STORAGE = new InjectionToken<Storage>(
  'MARKUP_STORE_STORAGE',
  {
    factory: () => window.sessionStorage,
  },
);

const markupStorageKey = 'lassi.markup';
const markupLastUsedStylesStorageKey = 'lassi.markup.last-used-styles';

function saveMarkup(markup: MarkupElement[], storage: Storage) {
  storage.setItem(markupStorageKey, JSON.stringify(markup));
}

function loadMarkup(storage: Storage): MarkupElement[] {
  const loaded = storage.getItem(markupStorageKey);
  return loaded ? JSON.parse(loaded) : [];
}

function saveLastUsedStyles(styles: MarkupLastUsedStylesMap, storage: Storage) {
  storage.setItem(markupLastUsedStylesStorageKey, JSON.stringify(styles));
}

function loadLastUsedStyles(storage: Storage): MarkupLastUsedStylesMap {
  const loaded = storage.getItem(markupLastUsedStylesStorageKey);
  return loaded ? JSON.parse(loaded) : {};
}

export const MarkupStore = signalStore(
  {
    providedIn: 'root',
  },
  withState<{
    _isDrawing: MarkupTypes | null;
    _isEditing: MarkupElement['id'] | null;
    layerMap: Record<MarkupElement['id'], Layer>;
    lastUsedStyles: MarkupLastUsedStylesMap;
  }>({
    _isDrawing: null,
    _isEditing: null,
    layerMap: {},
    lastUsedStyles: {},
  }),
  withEntities<MarkupElement>(),
  withComputed(({ _isDrawing, _isEditing, entityMap }) => ({
    isMarkupActive: computed(() => !!(_isDrawing() ?? _isEditing())),
    selected: computed(() => {
      const editing = _isEditing();
      return editing === null ? null : entityMap()[editing];
    }),
    drawingMarkupType: computed(() => _isDrawing()),
  })),
  withProps(() => ({
    storage: inject(MARKUP_STORE_STORAGE),
    mapService: inject(MapService),
    markupService: inject(MarkupService),
  })),
  withMethods(({ mapService, ...store }) => {
    return {
      getLayer: (id: MarkupPointElement['id']) => {
        return store.layerMap()[id];
      },
      clearAll: () => {
        mapService.markupLayerGroup.eachLayer((layer) =>
          mapService.markupLayerGroup.removeLayer(layer),
        );
        patchState(store, removeAllEntities(), {
          layerMap: {},
          _isDrawing: null,
          _isEditing: null,
        });
      },
      selectMarkupType: (markupType: MarkupTypes | null) => {
        patchState(store, (oldStore) => {
          if (oldStore._isEditing) {
            const editingElement = oldStore.entityMap[oldStore._isEditing];
            return {
              _isEditing: null,
              _isDrawing:
                editingElement.type === markupType ? null : markupType,
            };
          }
          return {
            _isDrawing: oldStore._isDrawing === markupType ? null : markupType,
            _isEditing: null,
          };
        });
      },
      startDrawing: (
        markupType: MarkupTypes,
        handleCreateMarkupElement: (layer: Layer) => {
          element: MarkupElement;
          layer: Layer;
        },
      ) => {
        console.log('start drawing');
        const handleCreate: PM.CreateEventHandler = (e) => {
          mapService.map.pm.disableDraw();

          const { element, layer } = handleCreateMarkupElement(e.layer);
          layer.addTo(mapService.markupLayerGroup);

          patchState(store, addEntity(element), (oldStore) => ({
            _isDrawing: null,
            _isEditing: element.id,
            layerMap: {
              ...oldStore.layerMap,
              [element.id]: layer,
            },
          }));
        };

        mapService.map.once('pm:create', handleCreate);

        mapService.map.pm.enableDraw(getGeomanShape(markupType), {
          finishOn: markupType === MarkupTypes.Callout ? 'click' : 'dblclick',
          markerStyle: {
            icon: divIcon({
              className: 'empty-divicon',
            }),
          },
        });
      },
      stopDrawing: () => {
        mapService.map.off('pm:create');
        mapService.map.pm.disableDraw();
      },
      updateElement: (
        element: Pick<MarkupElement, 'id'> & Partial<Omit<MarkupElement, 'id'>>,
      ) => {
        patchState(
          store,
          updateEntity({
            id: element.id,
            changes: element,
          }),
        );
      },
      selectMarkupElement: (layer: Layer) => {
        const elementId = Object.entries(store.layerMap()).find(
          ([_, l]) => l === layer,
        )?.[0];
        if (!elementId) {
          throw new Error('Selected element not found');
        }

        patchState(store, {
          _isDrawing: null,
          _isEditing: elementId,
        });
      },
      deleteMarkupElement: (id: MarkupElement['id']) => {
        patchState(
          store,
          removeEntity(id),
          {
            _isDrawing: null,
            _isEditing: null,
          },
          (state) => {
            const layer = state.layerMap[id];
            mapService.markupLayerGroup.removeLayer(layer);
            delete state.layerMap[id];
            return {
              layerMap: {
                ...state.layerMap,
              },
            };
          },
        );
      },
      saveLastUsedStyles: (element: MarkupElement) => {
        // Strip identity/geometry fields AND content fields (name) — only persist style attrs.
        const {
          id: _id,
          type,
          geometry: _geo,
          name: _name,
          ...attrs
        } = element as unknown as {
          id: string;
          type: MarkupTypes;
          geometry: unknown;
          name?: string;
        } & Record<string, unknown>;
        patchState(store, (state) => ({
          lastUsedStyles: {
            ...state.lastUsedStyles,
            [type]: attrs,
          } as MarkupLastUsedStylesMap,
        }));
      },
      clearLastUsedStyles: (type: MarkupTypes) => {
        patchState(store, (state) => {
          const updated = { ...state.lastUsedStyles };
          delete updated[type];
          return { lastUsedStyles: updated };
        });
      },
    };
  }),
  withHooks({
    onInit: ({
      entities,
      storage,
      mapService,
      markupService,
      lastUsedStyles,
      ...store
    }) => {
      const loaded = loadMarkup(storage);
      const layerMap = drawLoadedMarkup(
        loaded,
        markupService,
        mapService.markupLayerGroup,
      );
      const loadedStyles = loadLastUsedStyles(storage);
      patchState(store, addEntities(loaded), {
        layerMap,
        lastUsedStyles: loadedStyles,
      });

      effect(() => {
        saveMarkup(entities(), storage);
      });

      effect(() => {
        saveLastUsedStyles(lastUsedStyles(), storage);
      });
    },
  }),
);

function getGeomanShape(markupType: MarkupTypes) {
  switch (markupType) {
    case MarkupTypes.Point:
    case MarkupTypes.Text:
      return 'Marker';
    case MarkupTypes.Line:
    case MarkupTypes.Callout:
      return 'Line';
    case MarkupTypes.Polygon:
      return 'Polygon';
    case MarkupTypes.Rectangle:
      return 'Rectangle';
    default:
      throw new Error(`Geoman shape not implemented ${markupType}`);
  }
}

function drawLoadedMarkup(
  markupElements: MarkupElement[],
  markupService: MarkupService,
  layerGroup: LayerGroup,
) {
  const layerMap: Record<MarkupElement['id'], Layer> = {};
  markupElements.forEach((element) => {
    switch (element.type) {
      case MarkupTypes.Point: {
        const pointMarker = new Marker(
          latLng(
            element.geometry.geometry.coordinates[1],
            element.geometry.geometry.coordinates[0],
          ),
          {
            icon: markupService.createPointIcon(element, true),
          },
        );

        pointMarker.addTo(layerGroup);
        layerMap[element.id] = pointMarker;
        break;
      }

      case MarkupTypes.Line: {
        const polyLine = new Polyline(lineToShape(element.geometry.geometry), {
          interactive: false,
        });

        markupService.setLineStyles(element, polyLine);

        polyLine.addTo(layerGroup);
        layerMap[element.id] = polyLine;
        break;
      }

      case MarkupTypes.Polygon: {
        const polygon = new Polygon(polygonToShape(element.geometry.geometry), {
          interactive: false,
        });

        markupService.setPolygonStyles(element, polygon);

        polygon.addTo(layerGroup);
        layerMap[element.id] = polygon;
        break;
      }

      case MarkupTypes.Rectangle: {
        const rectangle = new Rectangle(
          latLngBounds(polygonToShape(element.geometry.geometry)),
          {
            interactive: false,
          },
        );

        markupService.setPolygonStyles(element, rectangle);

        rectangle.addTo(layerGroup);
        layerMap[element.id] = rectangle;
        break;
      }

      case MarkupTypes.Text: {
        const pointMarker = new Marker(
          latLng(
            element.geometry.geometry.coordinates[1],
            element.geometry.geometry.coordinates[0],
          ),
          {
            icon: markupService.createTextIcon(element, true),
          },
        );

        pointMarker.addTo(layerGroup);
        layerMap[element.id] = pointMarker;
        break;
      }

      case MarkupTypes.Callout: {
        const polyLine = new Polyline(lineToShape(element.geometry.geometry), {
          interactive: false,
        });

        const group = markupService.createCallout(element, polyLine, true);

        group.addTo(layerGroup);
        layerMap[element.id] = group;
        break;
      }

      default:
        throw new Error(
          `Drawing loaded markup type not implemented ${JSON.stringify(element)}`,
        );
    }
  });
  return layerMap;
}

function lineToShape(
  json: GeoJSON.LineString | GeoJSON.MultiLineString,
): LatLngExpression[] {
  if (json.type === 'LineString') {
    return json.coordinates.map(([lng, lat]) => [lat, lng]);
  } else if (json.type === 'MultiLineString') {
    return json.coordinates
      .map((coords) => coords.map(([lng, lat]) => [lat, lng] as LatLngTuple))
      .flat();
  }

  return [];
}

function polygonToShape(
  json: GeoJSON.Polygon | GeoJSON.MultiPolygon,
): LatLngExpression[] {
  if (json.type === 'Polygon') {
    return json.coordinates.flatMap((coords) =>
      coords.map(([lng, lat]) => [lat, lng] as LatLngTuple),
    );
  } else if (json.type === 'MultiPolygon') {
    return json.coordinates.flatMap((coords) =>
      coords.flatMap((coord) =>
        coord.map(([lng, lat]) => [lat, lng] as LatLngTuple),
      ),
    );
  }

  return [];
}
